import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry';

interface UploadResult {
  documentId: string;
  versionId: string;
  storagePath: string;
}

type InsertDocumentRpcRow = {
  document_id?: string | null;
  version_id?: string | null;
};

function normalizeInsertDocumentResponse(
  value: InsertDocumentRpcRow | InsertDocumentRpcRow[] | null,
) {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }

  return value ?? null;
}

export function useUploadDocument() {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function uploadDocument(
    file: File,
    supplierId: string,
    uploadedBy: string,
  ): Promise<UploadResult | null> {
    setUploading(true);
    setError(null);

    try {
      // 1. Prepare storage path
      const versionId = uuidv4(); // temporary id for the storage path
      const storagePath = `supplier-${supplierId}/${versionId}-${file.name}`;

      console.log('Uploading file ' + file.name + ' user:' + uploadedBy);

      // 2. Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // 3. Call the backend RPC function to insert document/version
      const { data, error: rpcError } = await supabase.rpc(
        'insert_document_with_version',
        {
          p_supplier_id: supplierId,
          p_title: file.name,
          p_file_name: file.name,
          p_storage_path: storagePath,
          p_mime_type: file.type,
          p_size_bytes: file.size,
          p_uploaded_by: uploadedBy,
        },
      );

      if (rpcError) throw rpcError;

      const documentRecord = normalizeInsertDocumentResponse(
        (data ?? null) as InsertDocumentRpcRow | InsertDocumentRpcRow[] | null,
      );

      if (!documentRecord?.document_id || !documentRecord?.version_id) {
        throw new Error(
          'Document upload completed, but the document record could not be created.',
        );
      }

      // 4. Return the IDs and storage path
      return {
        documentId: documentRecord.document_id,
        versionId: documentRecord.version_id,
        storagePath,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, { op: 'documents.upload' });
      console.error('Upload failed:', err);
      setError(err.message ?? 'Upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  }

  return { uploadDocument, uploading, error };
}
